import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
    UserPlus, Wallet, BookOpen, CheckCircle2, Loader2, Copy, FileText, AlertTriangle,
    CalendarRange, Home,
} from "lucide-react";
import { toast } from "sonner";

const BLANK = {
    email: "",
    name: "",
    password: "",
    program_id: "",
    payment_mode: "paid_offline",
    residency: "domestic",
    notes: "",
    send_welcome_email: true,
    // LOA-related profile + window
    dob: "",
    address_line1: "",
    address_city: "",
    address_province: "",
    address_postal_code: "",
    address_country: "Canada",
    start_date: "",
    end_date: "",
    generate_loa: false,
    email_loa: true,
};

/**
 * Admin modal for enrolling a student who paid offline at the college.
 *  - Auto-fills name if the email already belongs to a user.
 *  - Optional date-of-birth + mailing address + program access window so the
 *    backend can issue a PTIB-compliant Letter of Acceptance in the same trip.
 *  - On success shows the generated temp password (if a new account was created)
 *    and a "Download LOA" button when the LOA was issued.
 */
export default function ManualEnrollDialog({ open, onOpenChange, programs = [], onDone }) {
    const [form, setForm] = useState(BLANK);
    const [saving, setSaving] = useState(false);
    const [result, setResult] = useState(null);
    const [existingUser, setExistingUser] = useState(null);
    const [lookupDebounce, setLookupDebounce] = useState(null);
    const [issuingLoa, setIssuingLoa] = useState(false);

    useEffect(() => {
        if (open) {
            setForm(BLANK);
            setResult(null);
            setExistingUser(undefined);
        }
    }, [open]);

    // Debounced email lookup — if the email matches an existing user, pull
    // their name + DOB + address into the form so the admin can confirm and
    // re-use them for the LOA without retyping.
    useEffect(() => {
        if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            setExistingUser(undefined);
            return undefined;
        }
        if (lookupDebounce) window.clearTimeout(lookupDebounce);
        const t = window.setTimeout(async () => {
            try {
                const { data } = await api.get("/admin/users", { params: { include_deleted: false } });
                const hit = (data || []).find(
                    (u) => u.email?.toLowerCase() === form.email.toLowerCase(),
                );
                if (hit) {
                    setExistingUser({ id: hit.id, email: hit.email, name: hit.name });
                    setForm((f) => ({
                        ...f,
                        name: hit.name || f.name,
                        dob: hit.dob || f.dob,
                        address_line1: hit.address_line1 || f.address_line1,
                        address_city: hit.address_city || f.address_city,
                        address_province: hit.address_province || f.address_province,
                        address_postal_code: hit.address_postal_code || f.address_postal_code,
                        address_country: hit.address_country || f.address_country,
                    }));
                } else {
                    setExistingUser(null);
                }
            } catch {
                setExistingUser(undefined);
            }
        }, 400);
        setLookupDebounce(t);
        return () => window.clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.email]);

    const profileComplete = !!(form.dob && form.address_line1.trim());
    const canIssueLoa = profileComplete && !!form.start_date;

    const submit = async () => {
        if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            toast.error("Enter a valid email address.");
            return;
        }
        if (!existingUser && !form.name.trim()) {
            toast.error("A name is required for new student accounts.");
            return;
        }
        if (!form.program_id) {
            toast.error("Pick a program to enrol the student in.");
            return;
        }
        if (form.generate_loa && !canIssueLoa) {
            toast.error("Date of birth, address, and program start date are required to issue a Letter of Acceptance.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                email: form.email.trim().toLowerCase(),
                name: form.name.trim() || undefined,
                password: form.password.trim() || undefined,
                program_id: form.program_id,
                payment_mode: form.payment_mode,
                residency: form.residency,
                notes: form.notes.trim() || undefined,
                send_welcome_email: form.send_welcome_email,
                generate_loa: !!form.generate_loa,
                email_loa: !!form.email_loa,
                dob: form.dob || undefined,
                address_line1: form.address_line1.trim() || undefined,
                address_city: form.address_city.trim() || undefined,
                address_province: form.address_province.trim() || undefined,
                address_postal_code: form.address_postal_code.trim() || undefined,
                address_country: form.address_country.trim() || undefined,
                start_date: form.start_date || undefined,
                end_date: form.end_date || undefined,
            };
            const { data } = await api.post("/admin/enrollments/manual", payload);
            setResult(data);
            toast.success(
                data.user.created ? "Student account created & enrolled" : "Existing student enrolled",
            );
            if (data.loa?.error) {
                toast.warning(`LOA not issued: ${data.loa.error}`);
            } else if (data.loa?.emailed_to) {
                toast.success(`LOA ${data.loa.loa_number} emailed to ${data.loa.emailed_to}`);
            }
            onDone?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not enrol student.");
        } finally {
            setSaving(false);
        }
    };

    const issueLoaAfterEnrol = async (alsoEmail = true) => {
        if (!result?.enrollment_id) return;
        setIssuingLoa(true);
        try {
            const { data } = await api.post(`/admin/enrollments/${result.enrollment_id}/loa`, { email_loa: alsoEmail });
            setResult((r) => ({
                ...r,
                loa: {
                    loa_number: data.loa.loa_number,
                    loa_id: data.loa.id,
                    download_url: data.download_url,
                    emailed_to: data.loa.emailed_to,
                },
            }));
            toast.success(
                data.loa.emailed_to
                    ? `LOA ${data.loa.loa_number} issued & emailed to ${data.loa.emailed_to}`
                    : `LOA ${data.loa.loa_number} issued.`,
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not issue LOA.");
        } finally {
            setIssuingLoa(false);
        }
    };

    const downloadLoa = async (downloadUrl, loaNumber) => {
        try {
            const resp = await api.get(downloadUrl.replace(/^\/api/, ""), { responseType: "blob" });
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${loaNumber || "letter-of-acceptance"}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Could not download LOA.");
        }
    };

    const copy = (text) => {
        navigator.clipboard?.writeText(text);
        toast.success("Copied to clipboard");
    };

    const canShowNewAccountFields = existingUser !== undefined ? !existingUser : true;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl max-h-[92vh] overflow-y-auto"
                data-testid="manual-enroll-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <UserPlus className="h-5 w-5 text-primary" />
                        Enrol walk-in student
                    </DialogTitle>
                    <DialogDescription>
                        For students who paid at the college counter. We&rsquo;ll create their account
                        (or find an existing one), enrol them in the selected program, and optionally
                        issue a PTIB-compliant Letter of Acceptance.
                    </DialogDescription>
                </DialogHeader>

                {result ? (
                    <SuccessPanel
                        result={result}
                        onCopy={copy}
                        onClose={() => onOpenChange(false)}
                        onIssueLoa={() => issueLoaAfterEnrol(true)}
                        onResendLoa={() => issueLoaAfterEnrol(true)}
                        onDownloadLoa={downloadLoa}
                        issuingLoa={issuingLoa}
                    />
                ) : (
                    <div className="space-y-4">
                        {/* Email */}
                        <div>
                            <Label>Student email</Label>
                            <Input
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                placeholder="student@example.com"
                                data-testid="manual-enroll-email"
                            />
                            {existingUser ? (
                                <p
                                    className="text-xs text-primary mt-1 flex items-center gap-1.5"
                                    data-testid="manual-enroll-existing-hint"
                                >
                                    <CheckCircle2 className="h-3.5 w-3.5" />
                                    Existing student account: <strong>{existingUser.name}</strong>
                                </p>
                            ) : existingUser === null ? (
                                <p
                                    className="text-xs text-muted-foreground mt-1"
                                    data-testid="manual-enroll-new-hint"
                                >
                                    No account yet — we&rsquo;ll create one.
                                </p>
                            ) : null}
                        </div>

                        {canShowNewAccountFields && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label>Full name</Label>
                                    <Input
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        placeholder="Jasmine Patel"
                                        data-testid="manual-enroll-name"
                                    />
                                </div>
                                <div>
                                    <Label>
                                        Temporary password
                                        <span className="text-xs text-muted-foreground ml-1.5">
                                            (optional)
                                        </span>
                                    </Label>
                                    <Input
                                        value={form.password}
                                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                                        placeholder="leave blank for auto"
                                        data-testid="manual-enroll-password"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Program */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="sm:col-span-2">
                                <Label>Program</Label>
                                <Select
                                    value={form.program_id}
                                    onValueChange={(v) => setForm({ ...form, program_id: v })}
                                >
                                    <SelectTrigger data-testid="manual-enroll-program-trigger">
                                        <SelectValue placeholder="Pick a program" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {programs.map((p) => (
                                            <SelectItem key={p.id} value={p.id}>
                                                <div className="flex items-center gap-2">
                                                    <BookOpen className="h-3.5 w-3.5 text-muted-foreground" />
                                                    {p.title}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="flex items-center gap-1.5">
                                    <Wallet className="h-3.5 w-3.5 text-muted-foreground" />
                                    Payment mode
                                </Label>
                                <Select
                                    value={form.payment_mode}
                                    onValueChange={(v) => setForm({ ...form, payment_mode: v })}
                                >
                                    <SelectTrigger data-testid="manual-enroll-payment-trigger">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="paid_offline">Paid offline</SelectItem>
                                        <SelectItem value="waived">Waived / scholarship</SelectItem>
                                        <SelectItem value="online_stripe">Marked online</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Residency</Label>
                                <Select
                                    value={form.residency}
                                    onValueChange={(v) => setForm({ ...form, residency: v })}
                                >
                                    <SelectTrigger data-testid="manual-enroll-residency-trigger">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="domestic">Domestic</SelectItem>
                                        <SelectItem value="international">International</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Program window */}
                        <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3">
                            <Label className="flex items-center gap-1.5 text-sm font-medium">
                                <CalendarRange className="h-3.5 w-3.5 text-muted-foreground" />
                                Program access window
                                <span className="text-xs text-muted-foreground font-normal ml-auto">
                                    Required for LOA
                                </span>
                            </Label>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs text-muted-foreground">Start date</Label>
                                    <Input
                                        type="date"
                                        value={form.start_date}
                                        onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                                        data-testid="manual-enroll-start-date"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">Anticipated end</Label>
                                    <Input
                                        type="date"
                                        value={form.end_date}
                                        onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                                        data-testid="manual-enroll-end-date"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Profile (DOB + Address) */}
                        <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3">
                            <Label className="flex items-center gap-1.5 text-sm font-medium">
                                <Home className="h-3.5 w-3.5 text-muted-foreground" />
                                Student profile
                                <span className="text-xs text-muted-foreground font-normal ml-auto">
                                    Required for LOA
                                </span>
                            </Label>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs text-muted-foreground">Date of birth</Label>
                                    <Input
                                        type="date"
                                        value={form.dob}
                                        onChange={(e) => setForm({ ...form, dob: e.target.value })}
                                        data-testid="manual-enroll-dob"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">Country</Label>
                                    <Input
                                        value={form.address_country}
                                        onChange={(e) => setForm({ ...form, address_country: e.target.value })}
                                        placeholder="Canada"
                                        data-testid="manual-enroll-country"
                                    />
                                </div>
                                <div className="col-span-2">
                                    <Label className="text-xs text-muted-foreground">Mailing address</Label>
                                    <Input
                                        value={form.address_line1}
                                        onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
                                        placeholder="123 Cambridge Way"
                                        data-testid="manual-enroll-address-line1"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">City</Label>
                                    <Input
                                        value={form.address_city}
                                        onChange={(e) => setForm({ ...form, address_city: e.target.value })}
                                        placeholder="Surrey"
                                        data-testid="manual-enroll-city"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">Province / state</Label>
                                    <Input
                                        value={form.address_province}
                                        onChange={(e) => setForm({ ...form, address_province: e.target.value })}
                                        placeholder="BC"
                                        data-testid="manual-enroll-province"
                                    />
                                </div>
                                <div className="col-span-2">
                                    <Label className="text-xs text-muted-foreground">Postal / ZIP</Label>
                                    <Input
                                        value={form.address_postal_code}
                                        onChange={(e) => setForm({ ...form, address_postal_code: e.target.value })}
                                        placeholder="V3T 0A1"
                                        data-testid="manual-enroll-postal"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Notes */}
                        <div>
                            <Label>Notes (optional)</Label>
                            <Textarea
                                rows={2}
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                placeholder='e.g. "Paid $299 cash — receipt #A-1042"'
                                data-testid="manual-enroll-notes"
                            />
                        </div>

                        <div className="space-y-2 pt-1">
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="send-welcome"
                                    checked={form.send_welcome_email}
                                    onCheckedChange={(v) => setForm({ ...form, send_welcome_email: !!v })}
                                    data-testid="manual-enroll-send-welcome"
                                />
                                <Label htmlFor="send-welcome" className="cursor-pointer text-sm font-normal">
                                    Email the student their login + welcome
                                </Label>
                            </div>
                            <div className="flex items-start gap-2">
                                <Checkbox
                                    id="gen-loa"
                                    checked={form.generate_loa}
                                    onCheckedChange={(v) => setForm({ ...form, generate_loa: !!v })}
                                    data-testid="manual-enroll-generate-loa"
                                />
                                <div className="flex-1">
                                    <Label htmlFor="gen-loa" className="cursor-pointer text-sm font-normal flex items-center gap-1.5">
                                        <FileText className="h-3.5 w-3.5 text-primary" />
                                        Issue a Letter of Acceptance (PTIB-compliant PDF)
                                    </Label>
                                    {form.generate_loa && !canIssueLoa ? (
                                        <p
                                            className="text-xs text-amber-700 mt-1 flex items-start gap-1"
                                            data-testid="manual-enroll-loa-warning"
                                        >
                                            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                                            <span>
                                                Date of birth, mailing address, and program start date are required
                                                before the LOA can be issued.
                                            </span>
                                        </p>
                                    ) : null}
                                </div>
                            </div>
                            {form.generate_loa ? (
                                <div className="flex items-center gap-2 pl-6">
                                    <Checkbox
                                        id="email-loa"
                                        checked={form.email_loa}
                                        onCheckedChange={(v) => setForm({ ...form, email_loa: !!v })}
                                        data-testid="manual-enroll-email-loa"
                                    />
                                    <Label htmlFor="email-loa" className="cursor-pointer text-xs font-normal text-muted-foreground">
                                        Email the LOA PDF to the student as an attachment
                                    </Label>
                                </div>
                            ) : null}
                        </div>
                    </div>
                )}

                {!result ? (
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={saving}
                            data-testid="manual-enroll-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={submit}
                            disabled={saving}
                            className="bg-primary hover:bg-primary/90 gap-1.5"
                            data-testid="manual-enroll-submit"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Enrolling…
                                </>
                            ) : (
                                <>
                                    <UserPlus className="h-4 w-4" />
                                    Enrol student
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}

function SuccessPanel({ result, onCopy, onClose, onIssueLoa, onDownloadLoa, onResendLoa, issuingLoa }) {
    const loa = result.loa && !result.loa.error ? result.loa : null;
    const loaError = result.loa?.error;
    return (
        <div
            className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3"
            data-testid="manual-enroll-success"
        >
            <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" />
                <div className="flex-1">
                    <h3 className="font-display text-lg text-primary">
                        {result.user.created ? "Student account created" : "Student enrolled"}
                    </h3>
                    <p className="text-sm text-foreground/85">
                        <strong>{result.user.name}</strong> ({result.user.email}) is now enrolled in
                        <em className="ml-1">{result.program.title}</em>.
                    </p>
                    <div className="flex gap-2 mt-2 flex-wrap">
                        <Badge variant="secondary" className="text-xs capitalize">
                            Payment · {result.payment_mode.replace(/_/g, " ")}
                        </Badge>
                        {result.email_sent ? (
                            <Badge variant="outline" className="text-xs border-primary/40 text-primary">
                                Welcome email sent
                            </Badge>
                        ) : null}
                        {loa ? (
                            <Badge variant="outline" className="text-xs border-amber-500/40 text-amber-700">
                                LOA · {loa.loa_number}
                            </Badge>
                        ) : null}
                    </div>
                </div>
            </div>

            {result.temporary_password ? (
                <div className="rounded-md bg-background border border-border p-3">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                        Temporary password
                    </div>
                    <div className="flex items-center gap-2">
                        <code
                            className="flex-1 font-mono text-sm bg-muted/60 rounded px-2 py-1"
                            data-testid="manual-enroll-temp-password"
                        >
                            {result.temporary_password}
                        </code>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onCopy(result.temporary_password)}
                            className="gap-1.5"
                            data-testid="manual-enroll-copy-password"
                        >
                            <Copy className="h-3.5 w-3.5" />
                            Copy
                        </Button>
                    </div>
                </div>
            ) : null}

            {/* LOA panel */}
            <div className="rounded-md bg-background border border-border p-3">
                <div className="flex items-start gap-2">
                    <FileText className="h-4 w-4 text-primary mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">Letter of Acceptance</div>
                        {loa ? (
                            <>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {loa.loa_number} — ready to download.
                                    {loa.emailed_to ? (
                                        <span className="block text-primary mt-0.5">
                                            Emailed to {loa.emailed_to}
                                        </span>
                                    ) : null}
                                </p>
                                <div className="mt-2 flex gap-2 flex-wrap">
                                    <Button
                                        size="sm"
                                        onClick={() => onDownloadLoa(loa.download_url, loa.loa_number)}
                                        className="bg-primary hover:bg-primary/90 gap-1.5"
                                        data-testid="manual-enroll-loa-download"
                                    >
                                        <FileText className="h-3.5 w-3.5" />
                                        Download LOA PDF
                                    </Button>
                                    {!loa.emailed_to ? (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={onResendLoa}
                                            className="gap-1.5"
                                            data-testid="manual-enroll-loa-resend"
                                        >
                                            <FileText className="h-3.5 w-3.5" />
                                            Email it to student
                                        </Button>
                                    ) : null}
                                </div>
                            </>
                        ) : loaError ? (
                            <>
                                <p className="text-xs text-amber-700 mt-0.5 flex items-start gap-1">
                                    <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                                    <span>{loaError}</span>
                                </p>
                                <p className="text-xs text-muted-foreground mt-1.5">
                                    Edit the student&rsquo;s profile to add the missing details, then re-issue from the Students page.
                                </p>
                            </>
                        ) : (
                            <>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    Not issued yet — generate now if the student needs proof of acceptance.
                                </p>
                                <div className="mt-2">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={onIssueLoa}
                                        disabled={issuingLoa}
                                        className="gap-1.5"
                                        data-testid="manual-enroll-loa-issue-now"
                                    >
                                        {issuingLoa ? (
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        ) : (
                                            <FileText className="h-3.5 w-3.5" />
                                        )}
                                        Issue LOA now
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="pt-1 flex justify-end">
                <Button onClick={onClose} className="bg-primary hover:bg-primary/90" data-testid="manual-enroll-done">
                    Done
                </Button>
            </div>
        </div>
    );
}
